import React, { useEffect, useRef, useState } from "react";
import { novelApprovedSpread, novelApprovedSpreadCount } from "../utils/novelApprovedBookSpreadsV1";
import { loadNovelApprovedPdfV1 } from "../utils/loadNovelApprovedPdfV1";

// The source is a same-document blob URL supplied ONLY after the parent's
// existing SHA-256 verification of the approved, immutable interior PDF.
// PDF.js runs in this browser; this component never calls the NextScenes API.
function ApprovedPage({ document, pageNumber, isFR, leafStyle, pageRatio }) {
  const ref = useRef(null);
  const [issue, setIssue] = useState("");
  useEffect(() => {
    if (!document || !pageNumber || !ref.current) return undefined;
    let cancelled = false;
    let renderTask = null;
    setIssue("");
    async function draw() {
      try {
        const page = await document.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1.6 });
        const canvas = ref.current;
        if (!canvas) return;
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const context = canvas.getContext("2d", { alpha: false });
        if (!context) throw new Error("Canvas unavailable");
        renderTask = page.render({ canvasContext: context, viewport });
        await renderTask.promise;
      } catch (error) {
        if (!cancelled && error?.name !== "RenderingCancelledException") {
          setIssue(isFR ? "Cette page ne peut pas être affichée." : "This page could not be displayed.");
        }
      }
    }
    draw();
    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
    };
  }, [document, pageNumber, isFR]);

  return (
    <div data-approved-book-page={pageNumber}
      data-approved-page-proportion={pageRatio.toFixed(5)}
      style={{ ...leafStyle, background: "#fff", minWidth: 0,
        boxShadow: "0 2px 7px rgba(0,0,0,0.12)" }}>
      {issue ? <p role="alert" style={{color:"#9a3412",padding:12}}>{issue}</p> : null}
      <canvas ref={ref} aria-label={isFR ? `Page ${pageNumber}` : `Page ${pageNumber}`}
        style={{ display: "block", width: "100%", height: "auto",
          maxWidth: "100%" }} />
    </div>
  );
}

export default function NovelApprovedBookReaderV1({ pdfUrl = "", isFR = false }) {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [physicalPage, setPhysicalPage] = useState(1);
  const [singlePage, setSinglePage] = useState(false);
  const [pageRatio, setPageRatio] = useState(2 / 3);
  const viewportRef = useRef(null);
  const label = (en, fr) => isFR ? fr : en;
  const urlValid = typeof pdfUrl === "string" && pdfUrl.startsWith("blob:");

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(max-width: 760px)");
    const update = () => setSinglePage(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  useEffect(() => {
    if (!urlValid) {
      setDocument(null); setError(""); setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let task = null;
    setDocument(null); setError(""); setLoading(true); setPhysicalPage(1);
    async function openExactPdf() {
      try {
        // Lazy-load the browser renderer only when the author selects book view.
        const loadingTask = await loadNovelApprovedPdfV1(pdfUrl);
        if (cancelled) { loadingTask.destroy(); return; }
        task = loadingTask;
        const saved = await task.promise;
        if (!cancelled) setDocument(saved);
      } catch (error) {
        // Test-only diagnostic; CRA removes this branch from production builds.
        if (process.env.NODE_ENV === "test") {
          console.error("SYNTHETIC_BOOK_READER_LOAD_FAILURE", error);
        }
        if (!cancelled) setError(isFR
          ? "Le mode livre ne peut pas ouvrir ce PDF. L'original reste disponible dans l'inspection PDF."
          : "Book view could not open this PDF. The original remains available under PDF Inspection.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    openExactPdf();
    return () => {
      cancelled = true;
      if (task) task.destroy();
    };
  }, [pdfUrl, urlValid, isFR]);

  // Match the exact PDF page dimensions, not a square white flex panel.
  // Rendering remains unscaled in PDF coordinates; only CSS presentation fits.
  useEffect(() => {
    if (!document) return undefined;
    let current = true;
    document.getPage(1).then(page => {
      const viewport = page.getViewport({ scale: 1 });
      const ratio = viewport?.width / viewport?.height;
      if (current && Number.isFinite(ratio) && ratio > 0 && ratio < 3) {
        setPageRatio(ratio);
      }
    }).catch(() => {}); // Existing page rendering reports any PDF failures.
    return () => { current = false; };
  }, [document]);

  if (!urlValid) return null;

  const total = document?.numPages || 0;
  const spreadIndex = Math.floor(physicalPage / 2);
  const spread = novelApprovedSpread(total, spreadIndex);
  const spreadCount = novelApprovedSpreadCount(total);
  const forward = singlePage ? physicalPage < total : spreadIndex < spreadCount - 1;
  const backward = singlePage ? physicalPage > 1 : spreadIndex > 0;
  const next = () => {
    if (forward) setPhysicalPage(singlePage ? physicalPage + 1 : (spreadIndex + 1) * 2);
  };
  const previous = () => {
    if (backward) setPhysicalPage(singlePage ? physicalPage - 1 : (spreadIndex <= 1 ? 1 : (spreadIndex - 1) * 2));
  };
  const handleKey = event => {
    if (event.target?.tagName === "BUTTON" || event.target?.tagName === "SELECT") return;
    if (event.key === "ArrowRight" && forward) { event.preventDefault(); next(); }
    if (event.key === "ArrowLeft" && backward) { event.preventDefault(); previous(); }
  };
  // Fit complete, proportionate PDF pages within BOTH viewport height and
  // available spread width. No PDF cropping, padding inside white leaf panels,
  // new pagination or change to print margins.
  const pageWidth = `min(${singlePage ? "calc(100% - 20px)" : "calc(50% - 22px)"}, ${(70 * pageRatio).toFixed(4)}vh, ${(840 * pageRatio).toFixed(2)}px)`;
  const leafStyle = {
    flex: "0 0 auto",
    width: pageWidth,
    aspectRatio: String(pageRatio),
  };
  const pageLabel = singlePage
    ? `${physicalPage} / ${total}`
    : spread.left && spread.right
      ? `${spread.left}–${spread.right} / ${total}`
      : `${spread.left || spread.right || 1} / ${total}`;

  return (
    <section data-novel-approved-book-reader="read-only" aria-label={label("Read as a Book", "Lire comme un livre")}
      tabIndex={0} onKeyDown={handleKey} ref={viewportRef}
      style={{display:"grid",gap:12,minWidth:0,outlineOffset:4}}>
      <p className="ns-card-meta" style={{margin:0}}>
        {label("Turn pages horizontally. Page 1 opens on the right; the approved PDF and its page order remain unchanged.",
          "Tournez les pages horizontalement. La page 1 s'ouvre à droite ; le PDF approuvé et l'ordre des pages restent inchangés.")}
      </p>
      {loading ? <p role="status">{label("Opening your approved book…", "Ouverture du livre approuvé…")}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {document && total > 0 ? <>
        <div data-novel-facing-pages={singlePage ? "single" : "facing"}
          style={{ display:"flex", alignItems:"center", justifyContent:"center",
            gap: singlePage ? 0 : 9, padding:"12px 10px", borderRadius:14,
            border:"1px solid #cbd5e1", background:"#e6e3dc",
            minHeight:260, maxWidth:"100%", overflow:"hidden" }}>
          {singlePage
            ? <ApprovedPage key={physicalPage} document={document} pageNumber={physicalPage} isFR={isFR}
                leafStyle={leafStyle} pageRatio={pageRatio} />
            : <>
                {spread.left
                  ? <ApprovedPage key={spread.left} document={document} pageNumber={spread.left} isFR={isFR}
                      leafStyle={leafStyle} pageRatio={pageRatio} />
                  : <div aria-label={label("Blank inside cover", "Intérieur de couverture vierge")}
                      data-novel-book-blank-left="true"
                      style={{...leafStyle,background:"#f6f3ed"}} />}
                <div aria-hidden="true" style={{width:5,flex:"0 0 5px",
                  borderRadius:5, background:"linear-gradient(90deg,#a19b8f,#eee9df,#8c857b)"}} />
                {spread.right
                  ? <ApprovedPage key={spread.right} document={document} pageNumber={spread.right} isFR={isFR}
                      leafStyle={leafStyle} pageRatio={pageRatio} />
                  : <div aria-label={label("Blank end leaf", "Page de fin vierge")}
                      style={{...leafStyle,background:"#f6f3ed"}} />}
              </>}
        </div>
        <nav aria-label={label("Book page turning", "Navigation entre les pages")}
          style={{display:"flex",flexWrap:"wrap",justifyContent:"center",alignItems:"center",gap:12}}>
          <button type="button" className="ns-button ns-button-outline" disabled={!backward}
            data-novel-turn-previous="true" onClick={previous}>
            {label("← Previous Pages", "← Pages précédentes")}
          </button>
          <strong aria-live="polite" data-novel-book-pages-label="true">{pageLabel}</strong>
          <button type="button" className="ns-button ns-button-primary" disabled={!forward}
            data-novel-turn-next="true" onClick={next}>
            {label("Next Pages →", "Pages suivantes →")}
          </button>
        </nav>
        <p className="ns-card-meta" style={{textAlign:"center",margin:0}}>
          {singlePage
            ? label("Single-page layout for smaller screens.", "Une seule page pour les petits écrans.")
            : label("Select the book area and use ← or →, or use the buttons to turn pages.",
                "Sélectionnez le livre et utilisez ← ou →, ou les boutons pour tourner les pages.")}
        </p>
      </> : null}
    </section>
  );
}
